"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header } from "@/components/header";
import { useState, useEffect } from "react";
import { useInView } from "@/hooks/useInView";
import { webInfoApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

// Phone number formatter with auto + detection
const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters except +
  const cleaned = value.replace(/[^\d+]/g, '');

  // Auto-add + if doesn't start with it and has digits
  let formatted = cleaned;
  if (cleaned && !cleaned.startsWith('+')) {
    formatted = '+' + cleaned;
  }

  // Now format with + prefix
  if (formatted.startsWith('+')) {
    const numbers = formatted.slice(1);
    if (numbers.length <= 2) return `+${numbers}`;
    if (numbers.length <= 5) return `+${numbers.slice(0, 2)} ${numbers.slice(2)}`;
    if (numbers.length <= 9) return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    return `+${numbers.slice(0, 2)} ${numbers.slice(2, 5)}-${numbers.slice(5, 9)}-${numbers.slice(9, 13)}`;
  }

  return cleaned;
};

interface WebInfo {
  id_web_info: string;
  email?: string | null;
  phone?: string | null;
  office_address?: string | null;
  office_address_url?: string | null;
  terms_of_service?: string | null;
  privacy_policy?: string | null;
}

function ErrorRow({ message, id }: { message: string; id: string }) {
  if (!message) return null;

  return (
    <div id={id} className="mt-2 flex items-start gap-2 text-red-600">
      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-red-600">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
          <path d="M11 7h2v7h-2V7Zm0 9h2v2h-2v-2Z" />
        </svg>
      </span>
      <p className="text-sm leading-5">{message}</p>
    </div>
  );
}

export default function Contact() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  // only for UI validation behavior (show error like the image only after blur / submit)
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    subject: false,
    message: false,
  });

  // Web info state
  const [webInfo, setWebInfo] = useState<WebInfo | null>(null);

  // Loading and success states for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [formRef, formInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [infoRef, infoInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  const toWaMeNumber = (raw?: string | null) => {
    if (!raw) return "6285117705703";

    // keep digits only
    let digits = raw.replace(/\D/g, "");

    // if starts with 0 => convert to 62...
    if (digits.startsWith("0")) digits = "62" + digits.slice(1);

    // if starts with 62 already, keep it
    // if starts with 8 (like 851...) => assume Indonesia and prefix 62
    if (digits.startsWith("8")) digits = "62" + digits;

    return digits || "6285117705703";
  };

  const labels = {
    en: {
      getInTouch: "Get in Touch",
      haveQuestion: "Have a question or want to work together? We'd love to hear from you.",
      contactInfo: "Contact Information",
      descriptionInfo: "Feel free to reach out to us through any of these channels.",
      name: "Name",
      subject: "Subject",
      message: "Message",
      yourName: "Your name",
      yourEmail: "your.email@example.com",
      howCanWeHelp: "How can we help?",
      yourMessage: "Your message",
      phone: "Phone",
      address: "Address",
      sendMessage: "Send Message",
    },
    id: {
      getInTouch: "Kontak Kami",
      haveQuestion: "Apakah Anda memiliki pertanyaan atau ingin bekerja sama? Kami senang mendengar dari Anda.",
      contactInfo: "Informasi Kontak",
      descriptionInfo: "Silakan hubungi kami melalui salah satu saluran ini.",
      name: "Nama",
      subject: "Judul",
      message: "Pesan",
      yourName: "Nama anda",
      yourEmail: "email.anda@contoh.com",
      howCanWeHelp: "Apa yang bisa kami bantu?",
      yourMessage: "Pesan Anda",
      phone: "Telepon",
      address: "Alamat",
      sendMessage: "Kirim Pesan",
    },
  };

  const t = labels[language];

  // Fetch web info on component mount
  useEffect(() => {
    const fetchWebInfo = async () => {
      try {
        const response = await webInfoApi.getAll();
        const webInfoData = response.data?.data || response.data;
        // Get the first web info record (assuming there's only one)
        if (Array.isArray(webInfoData) && webInfoData.length > 0) {
          setWebInfo(webInfoData[0]);
        } else if (webInfoData && !Array.isArray(webInfoData)) {
          setWebInfo(webInfoData);
        }
      } catch (error) {
        console.log('Failed to fetch web info:', error);
        // Set default values if API fails
        setWebInfo({
          id_web_info: 'default',
          email: 'info@ganeshait.com',
          phone: '6285117705703',
          office_address: 'Apartemen Grand Taman Melati 2 Ruko 2 Tower C, Pondok Cina, Depok',
          office_address_url: 'https://www.google.com/maps/search/Apartemen+Grand+Taman+Melati+2+Ruko+2+Tower+C+Pondok+Cina+Depok/@-6.3599536,106.8324149,17z?entry=s&sa=X&ved=1t:199789',
        });
      }
    };

    fetchWebInfo();
  }, []);

  // Capitalize first letter of each word (preserve spaces while typing)
  const capitalizeWords = (str: string) => {
    return str
      .toLowerCase()
      // split but KEEP whitespace tokens, so trailing spaces won't vanish
      .split(/(\s+)/)
      .map((part) => {
        // keep spaces exactly
        if (/^\s+$/.test(part)) return part;
        // capitalize non-space segments
        return part ? part.charAt(0).toUpperCase() + part.slice(1) : part;
      })
      .join("");
  };

  // Email validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // per-field validation (for blur + live when already touched)
  const validateField = (name: keyof typeof formData, value: string) => {
    if (name === "name") {
      if (!value.trim()) return "Name is required";
      return "";
    }

    if (name === "email") {
      if (!value.trim()) return "Email is required";
      if (!validateEmail(value))
        return "Please enter a valid email address (e.g., example@domain.com)";
      return "";
    }

    if (name === "subject") {
      if (!value.trim()) return "Subject is required";
      return "";
    }

    if (name === "message") {
      if (!value.trim()) return "Message is required";
      return "";
    }

    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "name") {
      // Auto-capitalize name field
      const capitalizedValue = capitalizeWords(value);
      setFormData({
        ...formData,
        [name]: capitalizedValue,
      });

      // if already touched, validate live
      if (touched.name) {
        setErrors((prev) => ({
          ...prev,
          name: validateField("name", capitalizedValue),
        }));
      } else if (errors.name) {
        setErrors({ ...errors, name: "" });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });

      // if already touched, validate live
      if (touched[name as keyof typeof touched]) {
        setErrors((prev) => ({
          ...prev,
          [name]: validateField(name as keyof typeof formData, value),
        }));
      } else if (errors[name as keyof typeof errors]) {
        setErrors({ ...errors, [name]: "" });
      }
    }
  };

  // show error like the image on blur
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    const key = name as keyof typeof formData;

    setTouched((prev) => ({ ...prev, [key]: true }));
    setErrors((prev) => ({
      ...prev,
      [key]: validateField(key, formData[key]),
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField("name", formData.name),
      email: validateField("email", formData.email),
      subject: validateField("subject", formData.subject),
      message: validateField("message", formData.message),
    };

    // mark all touched so UI shows all errors (required look)
    setTouched({
      name: true,
      email: true,
      subject: true,
      message: true,
    });

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      // Get the API base URL from environment or use default
      const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

      const response = await fetch(`${apiBaseUrl}/api/contact/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitError(data.detail || "Failed to send message. Please try again.");
        return;
      }

      // Success
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      setTouched({
        name: false,
        email: false,
        subject: false,
        message: false,
      });
      setErrors({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.log("Error sending contact form:", error);
      setSubmitError("An error occurred while sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // show error only when touched OR after submit validation
  const showError = (key: keyof typeof errors) => touched[key] && !!errors[key];

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        {/* Navigation Bar */}
        <Header activePage="home" />

        {/* Main Content */}
        <section className="pt-32 pb-16 px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Main Card */}
            <div ref={contentRef} className={`bg-white rounded-2xl border border-black/10 shadow-[0_10px_30px_rgba(0,0,0,0.12)] px-10 md:px-14 py-12 reveal ${contentInView ? 'is-visible' : ''}`}>
              {/* Center Title */}
              <div className={`text-center mb-10 reveal ${contentInView ? 'is-visible reveal-delay-100' : ''}`}>
                <h1 className="text-3xl md:text-4xl font-bold text-black mb-3">
                  {t.getInTouch}
                </h1>
                <p className="text-lg text-black">
                  {t.haveQuestion}
                </p>
              </div>

              {/* Two Column Layout */}
              <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-start">
                {/* Left Column - Contact Form */}
                <div ref={formRef} className={`md:pr-2 reveal ${formInView ? 'is-visible reveal-delay-200' : ''}`}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Field */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${showError("name") ? "text-red-600" : "text-black"
                          }`}
                      >
                        {t.name} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-black">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        </div>
                        <Input
                          type="text"
                          name="name"
                          placeholder={t.yourName}
                          value={formData.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={showError("name")}
                          aria-describedby={showError("name") ? "name-error" : undefined}
                          className={`pl-10 h-12 bg-white ${showError("name")
                            ? "border-red-600 focus-visible:border-red-600 focus-visible:ring-red-500/30"
                            : "border-gray-300"
                            }`}
                          required
                        />
                      </div>
                      <ErrorRow
                        id="name-error"
                        message={showError("name") ? errors.name : ""}
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${showError("email") ? "text-red-600" : "text-black"
                          }`}
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-black">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <Input
                          type="email"
                          name="email"
                          placeholder={t.yourEmail}
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={showError("email")}
                          aria-describedby={showError("email") ? "email-error" : undefined}
                          className={`pl-10 h-12 bg-white ${showError("email")
                            ? "border-red-600 focus-visible:border-red-600 focus-visible:ring-red-500/30"
                            : "border-gray-300"
                            }`}
                          required
                        />
                      </div>
                      <ErrorRow
                        id="email-error"
                        message={showError("email") ? errors.email : ""}
                      />
                    </div>

                    {/* Subject Field */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${showError("subject") ? "text-red-600" : "text-black"
                          }`}
                      >
                        {t.subject} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-black">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                        </div>
                        <Input
                          type="text"
                          name="subject"
                          placeholder={t.howCanWeHelp}
                          value={formData.subject}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={showError("subject")}
                          aria-describedby={
                            showError("subject") ? "subject-error" : undefined
                          }
                          className={`pl-10 h-12 bg-white ${showError("subject")
                            ? "border-red-600 focus-visible:border-red-600 focus-visible:ring-red-500/30"
                            : "border-gray-300"
                            }`}
                          required
                        />
                      </div>
                      <ErrorRow
                        id="subject-error"
                        message={showError("subject") ? errors.subject : ""}
                      />
                    </div>

                    {/* Message Field */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${showError("message") ? "text-red-600" : "text-black"
                          }`}
                      >
                        {t.message} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-4 text-black">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <textarea
                          name="message"
                          placeholder={t.yourMessage}
                          value={formData.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          rows={6}
                          aria-invalid={showError("message")}
                          aria-describedby={
                            showError("message") ? "message-error" : undefined
                          }
                          className={`w-full pl-10 pr-4 py-3 rounded-md bg-white text-base shadow-xs transition-[color,box-shadow] outline-none resize-none ${showError("message")
                            ? "border border-red-600 focus-visible:border-red-600 focus-visible:ring-red-500/30 focus-visible:ring-[3px]"
                            : "border border-gray-300 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                            }`}
                          required
                        />
                      </div>
                      <ErrorRow
                        id="message-error"
                        message={showError("message") ? errors.message : ""}
                      />
                    </div>

                    {/* Error Message Alert */}
                    {submitError && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-start gap-3">
                          <div className="text-red-600 mt-0.5">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-red-800">{submitError}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Success Message Alert */}
                    {submitSuccess && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-start gap-3">
                          <div className="text-green-600 mt-0.5">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-green-800">Your message has been sent successfully! We will get back to you soon.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 text-white font-semibold rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                      }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        t.sendMessage
                      )}
                    </Button>



                  </form>
                </div>

                {/* Right Column - Contact Information (raised cards + darker bottom shadow) */}
                <div ref={infoRef} className={`md:pl-2 reveal ${infoInView ? 'is-visible reveal-delay-300' : ''}`}>
                  <h2 className="text-xl font-bold text-black mb-1">
                    {t.contactInfo}
                  </h2>
                  <p className="text-gray-500 mb-6">
                    {t.descriptionInfo}
                  </p>

                  <div className="space-y-6">
                    {/* Email Card */}
                    {/* <a
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=info@gmail.com&su=Recruitment%20Inquiry&body=Hello%20Nasywaa,%0D%0A%0D%0AI%20would%20like%20to%20discuss%20recruitment%20opportunities."
                    className="block w-full"
                    aria-label="Open email in Gmail"
                  > */}
                    <a
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(webInfo?.email || 'info@ganeshait.com')}&su=Contact%20Support%20-%20GaneshaIT&body=Hi%20GaneshaIT%20Team,%0D%0A%0D%0AI%20would%20like%20to%20contact%20your%20support%20team%20regarding%20the%20following%20issue:%0D%0A%0D%0AIssue:%0D%0A%5BPlease%20describe%20your%20issue%20here%5D%0D%0A%0D%0ABest%20regards,%0D%0A%5BYour%20Name%5D`}
                      className="block w-full"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="w-full min-h-30 bg-white rounded-xl border border-black/10 p-6 flex items-center shadow-[0_10px_0_rgba(12,26,46,0.85),0_14px_22px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-[0.99] cursor-pointer">
                        <div className="flex items-start gap-7">
                          <div className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-[#0C2E6C]">
                            <svg
                              className="w-7 h-7 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-black mb-1">Email</h3>
                            <span className="text-gray-500 hover:text-[#285494] transition-colors">
                              {webInfo?.email || 'info@ganeshait.com'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>

                    {/* Phone Card */}
                    <a
                      href={`https://wa.me/${toWaMeNumber(webInfo?.phone)}`}
                      className="block w-full"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="w-full min-h-30 bg-white rounded-xl border border-black/10 p-6 flex items-center shadow-[0_10px_0_rgba(12,26,46,0.85),0_14px_22px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-[0.99] cursor-pointer">
                        <div className="flex items-start gap-7">
                          <div className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-[#0C2E6C]">
                            <svg
                              className="w-7 h-7 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-black mb-1">{t.phone}</h3>
                            <span className="text-gray-500 hover:text-[#285494] transition-colors">
                              {webInfo?.phone ? formatPhoneNumber(webInfo.phone) : '+62 851-1770-5703'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>

                    {/* Office Card */}
                    <a
                      href={webInfo?.office_address_url || "https://www.google.com/maps/search/Apartemen+Grand+Taman+Melati+2+Ruko+2+Tower+C+Pondok+Cina+Depok/@-6.3599536,106.8324149,17z?entry=s&sa=X&ved=1t:199789"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full"
                      aria-label="Open office location in Google Maps"
                    >
                      <div className="w-full min-h-30 bg-white rounded-xl border border-black/10 p-6 flex items-center shadow-[0_10px_0_rgba(12,26,46,0.85),0_14px_22px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-[0.99] cursor-pointer">
                        <div className="flex items-start gap-7">
                          <div className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-[#0C2E6C]">
                            <svg
                              className="w-7 h-7 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-black mb-1">{t.address}</h3>
                            <p className="text-gray-500">
                              {webInfo?.office_address || 'Apartemen Grand Taman Melati 2 Ruko 2 Tower C, Pondok Cina, Depok'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
                {/* end right column */}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}
